import * as React from "react";
import styled, { css } from "styled-components";
import theme from "styled-theming";
import colors from "../styles/colors";
import { ThemeType } from "../styles/theme";

const switchTextColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.white
});

const switchTextColorSelected = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
});

const switchBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
});

const switchBackgroundColorHover = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark2
});

const switchBackgroundColorSelected = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.dark2
});

const switchBackgroundColorDisabled = theme("mode", {
    [ThemeType.light]: colors.grey_4,
    [ThemeType.dark]: colors.dark3
});

const BaseSwitch = styled.button<ICustomStyledSwitchProps>`
    border: 2px solid ${colors.grey_2};
    background-color: ${switchBackgroundColor};
    color: ${switchTextColor};
    padding: 10px 15px;
    font-size: 1rem;
    cursor: pointer;
    font-family: "Gilroy-Medium", "medium-fallback";
    &:hover {
        background-color: ${switchBackgroundColorHover};
    }
    ${props => props.selected && props.disabled && css`
        cursor: not-allowed;
        background-color: ${switchBackgroundColorSelected};
    `}
    ${props => props.disabled && css`
        cursor: not-allowed;
        background-color: ${switchBackgroundColorDisabled};
        &:hover {
            background-color: ${switchBackgroundColorDisabled};
        }
    `}
    ${props => props.selected && css`
        background-color: ${switchBackgroundColorSelected};
        color: ${switchTextColorSelected};
        &:hover {
            background-color: ${switchBackgroundColorSelected};
        }
    `}
`

const FirstSwitch = styled(BaseSwitch)`
    border-radius: 20px 0px 0px 20px;
    border-right: 0px;
`

const LastSwitch = styled(BaseSwitch)`
    border-radius: 0px 20px 20px 0px;
`

const MiddleSwitch = styled(BaseSwitch)`
    border-radius: 0px;
    &:not(:last-of-type){
        border-right: 0px;
    }
`

const SwitchContainer = styled.div`
    display: flex;
`


const firstSwitch = (firstSwitch: ICustomSwitch) => (
    <FirstSwitch
        key={firstSwitch.text}
        onClick={firstSwitch.onClick}
        selected={firstSwitch.selected}
        disabled={firstSwitch.disabled}
        title={firstSwitch.title}
        type="button"
    >
        {firstSwitch.text}
    </FirstSwitch>
);

const lastSwitch = (lastSwitch: ICustomSwitch) => (
    <LastSwitch
        key={lastSwitch.text}
        onClick={lastSwitch.onClick}
        selected={lastSwitch.selected}
        disabled={lastSwitch.disabled}
        title={lastSwitch.title}
        type="button"
    >
        {lastSwitch.text}
    </LastSwitch>
)

const middleSwitch = (middleSwitch: ICustomSwitch) => (
    <MiddleSwitch
        key={middleSwitch.text}
        onClick={middleSwitch.onClick}
        selected={middleSwitch.selected}
        disabled={middleSwitch.disabled}
        title={middleSwitch.title}
        type="button"
    >
        {middleSwitch.text}
    </MiddleSwitch>
)

const CustomSwitch: React.FC<ICustomSwitchProps> = (props) => {

    const getRenderableSwitches = () => {
        if (props.switches.length < 2) {
            return null;
        } else if (props.switches.length === 2) {
            return [firstSwitch(props.switches[0]), lastSwitch(props.switches[1])];
        } else {
            const swithchesBetweenFirstAndLast = props.switches
                .slice(1, -1)
                .map((ms) => middleSwitch(ms));
            return [firstSwitch(props.switches[0]), ...swithchesBetweenFirstAndLast, lastSwitch(props.switches[props.switches.length - 1])];
        }
    }

    return (
        <SwitchContainer className="switch-container-selector" data-testid="switch">
            {getRenderableSwitches()}
        </SwitchContainer>
    );
}

export const CustomBooleanSwitch: React.FC<ICustomBooleanSwitchProps> = (props) => {

    const [toggled, setToggled] = React.useState<boolean | null>(props.toggled);

    React.useEffect(() => {
        if (toggled !== null && toggled !== props.toggled) {
            props.onChange(toggled);
        }
    }, [toggled]);

    const shouldFalseBeSelected = props.toggled !== null && !props.toggled;
    const switches: ICustomSwitch[] = [
        {
            text: props.trueLabel ?? "Yes",
            selected: props.toggled,
            onClick: () => setToggled(true)
        },
        {
            text: props.falseLabel ?? "No",
            selected: shouldFalseBeSelected,
            onClick: () => setToggled(false)
        }
    ]

    return (
        <SwitchContainer className="custom-boolean-switch">
            {firstSwitch(switches[0])}
            {lastSwitch(switches[1])}
        </SwitchContainer>
    )
}

interface ICustomStyledSwitchProps {
    selected: boolean | null;
}

export interface ICustomSwitch {
    text: string;
    onClick: () => void;
    selected: boolean | null;
    disabled?: boolean;
    title?: string;
}

interface ICustomSwitchProps {
    switches: ICustomSwitch[];
}

interface ICustomBooleanSwitchProps {
    toggled: boolean | null;
    disabled?: boolean;
    trueLabel?: string;
    falseLabel?: string;
    onChange: (newValue: boolean) => void;
}

export default CustomSwitch;